import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, Train, MapPin, Clock, Gauge, AlertCircle, Loader2, Navigation,
  Calendar, ArrowRight, TrendingUp, Timer, RefreshCw, CheckCircle2, Radio,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import Disclaimer from '../components/Disclaimer';
import ShareMenu from '../components/ShareMenu';
import { usePageMeta } from '../hooks/usePageMeta';

import { API } from '../config';

// ---- helpers ----
const parseDelayMin = (s) => {
  if (typeof s?.delayMinutes === 'number') return s.delayMinutes;
  const t = (s?.delay || '').toLowerCase();
  if (!t || t.includes('on time') || t.includes('right time')) return 0;
  let m = 0;
  const hr = t.match(/(\d+)\s*hr/); const mn = t.match(/(\d+)\s*min/);
  if (hr) m += parseInt(hr[1], 10) * 60;
  if (mn) m += parseInt(mn[1], 10);
  return m;
};
const fmtDelay = (min) => {
  if (!min || min <= 0) return 'On Time';
  if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}m late`;
  return `${min} min late`;
};
const cleanName = (s) => (s || '').replace(/\s*\([A-Z]+\)\s*$/, '').trim() || s;

const DAY_OPTIONS = [
  { id: 'today', value: '0', label: 'Today', icon: '📍' },
  { id: 'yesterday', value: '1', label: 'Yesterday', icon: '⏪' },
  { id: '2days_ago', value: '2', label: '2 Days Ago', icon: '⏮️' },
  { id: '3days_ago', value: '3', label: '3 Days Ago', icon: '⏮️' },
  { id: '4days_ago', value: '4', label: '4 Days Ago', icon: '⏮️' },
];

const LIVE_STATUS_FAQS = [
  {
    q: 'How can I check live train status online?',
    a: 'You can check live train status online by visiting the TrainPanda Live Train Status page. Use the available search option to find your train and view the latest available running information, including train position, delays, and expected arrival or departure details.'
  },
  {
    q: 'How can I check train running status by train number?',
    a: 'To check train running status by train number, visit TrainPanda Live Train Status and use the available train search option. Enter the required details, select your train, and check the latest available running status and route information.'
  },
  {
    q: 'Can I check Indian Railway train status and train delays online?',
    a: 'Yes, you can check the latest available Indian Railway train status online through TrainPanda. The information may include train running updates, reported delays, and expected arrival or departure timings. Actual timings can change during the journey, so check the latest updates before travelling.'
  },
  {
    q: 'What is the best way to check train arrival and departure status?',
    a: 'The best approach is to check the latest available train running information before leaving for the railway station. TrainPanda helps you access train arrival status, train departure status, and other available journey details so you can plan your station arrival more conveniently.'
  },
  {
    q: 'Is TrainPanda an alternative to NTES for live train tracking?',
    a: 'TrainPanda is another platform passengers can explore for convenient access to live train status and train running information. If you are looking for an NTES alternative or a live train tracker, visit TrainPanda to check the available train status details and plan your journey.'
  }
];

const LiveTrainStatus = () => {
  usePageMeta(
    'Live Train Status – Check Train Running Status | TrainPanda',
    'Check live train status, running updates, delays, arrival times and train location online with TrainPanda. Track your train easily in real time.'
  );
  const [params] = useSearchParams();
  const [trainNo, setTrainNo] = useState(params.get('train') || '');
  const [startDay, setStartDay] = useState(params.get('start_day') || params.get('day') || '0');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const fetchStatus = async (tn, isRefresh = false, dayToUse = startDay) => {
    if (!tn) return;
    setShowDropdown(false);
    isRefresh ? setRefreshing(true) : (setLoading(true), setStatus(null));
    setError('');
    try {
      const r = await axios.get(`${API}/live-status/${encodeURIComponent(tn.trim())}?start_day=${encodeURIComponent(dayToUse)}`);
      setStatus(r.data.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not fetch status. Please try again.');
    } finally { setLoading(false); setRefreshing(false); }
  };

  const handleDayChange = (dayVal) => {
    setStartDay(dayVal);
    if (trainNo && trainNo.trim()) {
      fetchStatus(trainNo.trim(), false, dayVal);
    }
  };

  useEffect(() => {
    if (params.get('train')) {
      const dayParam = params.get('start_day') || params.get('day') || '0';
      fetchStatus(params.get('train'), false, dayParam);
    }
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    const q = trainNo.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/trains/search?q=${encodeURIComponent(q)}&limit=8`);
        setSuggestions(res.data.results || []);
      } catch (err) {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [trainNo]);

  const selectTrain = (item) => {
    setTrainNo(item.number);
    setShowDropdown(false);
    fetchStatus(item.number);
  };

  const stops = status?.schedule || [];
  let currentIdx = stops.findIndex((s) => s.status === 'current' || s.status === 'arrived');
  if (currentIdx === -1) {
    const departed = stops.map((s) => s.status).lastIndexOf('departed');
    currentIdx = departed >= 0 ? departed : 0;
  }
  const nextIdx = currentIdx + 1 < stops.length ? currentIdx + 1 : -1;
  const stateFor = (i) => (i < currentIdx ? 'completed' : i === currentIdx ? 'current' : i === nextIdx ? 'next' : 'remaining');

  const progressPct = status
    ? (status.totalDistance > 0 && status.distanceCovered > 0
      ? Math.min(100, (status.distanceCovered / status.totalDistance) * 100)
      : stops.length > 1 ? (currentIdx / (stops.length - 1)) * 100 : 0)
    : 0;

  const delayMin = status?.delayMinutes ?? parseDelayMin(status || {});
  const onTime = delayMin <= 0;
  const journeyDate = stops[0]?.date || (status?.lastUpdated || '').split(' ').slice(0, 3).join(' ');
  const maxDelay = stops.reduce((m, s) => Math.max(m, parseDelayMin(s)), 0);
  const onTimeStops = stops.filter((s) => parseDelayMin(s) <= 0).length;

  const lastStation = stops[currentIdx] ? cleanName(stops[currentIdx].stationName || stops[currentIdx].station) : '—';
  const nextStation = nextIdx >= 0 ? cleanName(stops[nextIdx].stationName || stops[nextIdx].station) : '—';
  const nextEta = nextIdx >= 0 ? (stops[nextIdx].actArr || stops[nextIdx].schArr || stops[nextIdx].act || '—') : '—';

  const shareUrl = status ? `${window.location.origin}/live-train-status?train=${status.trainNumber}` : '';
  const shareTitle = status ? `${status.trainName} (${status.trainNumber}) — Live Status` : '';
  const shareText = status ? [
    `🚂 ${status.trainName} (${status.trainNumber})`,
    `📍 At: ${lastStation}`,
    nextStation !== '—' ? `➡️ Next: ${nextStation}${nextEta !== '—' ? ` (ETA ${nextEta})` : ''}` : '',
    `⏱️ ${fmtDelay(delayMin)}`,
    `Track live on TrainPanda 👇`,
  ].filter(Boolean).join('\n') : '';

  return (
    <div className="pb-10">
      {/* Search header with motion-blur train bg */}
      <div className="relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/train-bg.jpg)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A1259]/85 via-[#3a1d72]/80 to-transparent" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 pt-6 pb-5 z-20">
          <div className="flex items-center gap-2 text-tp-coral font-bold text-xs uppercase tracking-[0.2em]"><Radio size={14} className="lt-live-blink" /> Live Train Status</div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight mt-1">Track Your Train Live with Real-Time Running Status</h1>
          <p className="text-white/70 text-sm mt-0.5">Real-time position, delays, platform & full route.</p>
          <div className="mt-4 bg-white rounded-2xl p-2.5 flex flex-col sm:flex-row gap-2.5 shadow-[0_18px_40px_-12px_rgba(124,58,237,0.45)] relative z-30">
            <div className="flex items-center gap-2 border-2 border-gray-100 rounded-xl focus-within:border-tp-violet px-3 flex-1 transition-colors relative">
              <Train size={18} className="text-tp-violet shrink-0" />
              <Input
                value={trainNo}
                onChange={(e) => {
                  setTrainNo(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setShowDropdown(false);
                    fetchStatus(trainNo);
                  }
                }}
                placeholder="Train number or name e.g. 12002 or Bhopal Shatabdi"
                className="border-0 focus-visible:ring-0 h-11 text-base w-full"
                data-testid="live-train-input"
              />
              {trainNo && (
                <button
                  onClick={() => { setTrainNo(''); setSuggestions([]); }}
                  className="text-gray-400 hover:text-gray-600 text-xs px-1 font-semibold"
                >
                  ✕
                </button>
              )}

              {/* Suggestions Dropdown */}
              {showDropdown && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-1 z-50 max-h-72 overflow-y-auto">
                  {suggestions.map((item) => (
                    <div
                      key={item.number}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectTrain(item);
                      }}
                      className="px-4 py-2.5 hover:bg-violet-50/70 cursor-pointer flex items-center justify-between border-b border-gray-50 last:border-b-0 transition-colors"
                    >
                      <div className="flex-1 min-w-0 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-sm text-tp-violet bg-violet-100 px-2 py-0.5 rounded-md">
                            {item.number}
                          </span>
                          <span className="font-bold text-sm text-gray-800 truncate">
                            {item.name}
                          </span>
                        </div>
                        {(item.from || item.to) && (
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                            <span>{item.from} ({item.from_code || ''})</span>
                            <ArrowRight size={11} className="text-gray-400 shrink-0" />
                            <span>{item.to} ({item.to_code || ''})</span>
                          </div>
                        )}
                      </div>
                      <span className="text-[11px] font-bold text-tp-coral bg-orange-50 px-2 py-1 rounded-full border border-orange-100 shrink-0">
                        Track Live
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => fetchStatus(trainNo)} disabled={loading} data-testid="live-check-btn" className="btn-violet rounded-xl h-11 px-8 flex items-center justify-center gap-2 font-bold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Activity size={16} /> Track</>}
            </button>
          </div>

          {/* Quick Date Selection Pills */}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-white/80 font-semibold mr-1 flex items-center gap-1">
              <Calendar size={13} className="text-tp-coral" /> Journey Start Day:
            </span>
            {DAY_OPTIONS.map((d) => (
              <button
                key={d.id}
                onClick={() => handleDayChange(d.value)}
                data-testid={`day-pill-${d.id}`}
                className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all flex items-center gap-1 ${
                  startDay === d.value
                    ? 'bg-tp-coral text-white shadow-md scale-105 ring-2 ring-white/30'
                    : 'bg-white/15 text-white/90 hover:bg-white/25 backdrop-blur-sm'
                }`}
              >
                <span>{d.icon}</span>
                <span>{d.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl flex items-center gap-2" data-testid="live-error">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-24">
            <Loader2 className="animate-spin mx-auto text-tp-violet" size={40} />
            <p className="mt-3 text-gray-500 text-sm font-medium">Locating your train on the network…</p>
          </div>
        )}

        {status && !loading && stops.length === 0 && (
          <div className="bg-white rounded-3xl p-8 text-center card-shadow" data-testid="live-nodata">
            <Train size={36} className="mx-auto text-gray-300 mb-3" />
            <h3 className="font-bold text-tp-ink">{status.trainNumber} — live data unavailable</h3>
            <p className="text-sm text-gray-500 mt-1">{status.currentStation || 'Schedule could not be fetched right now. Please try again shortly.'}</p>
          </div>
        )}

        {status && !loading && stops.length > 0 && (
          <div className="space-y-5">
            {/* ===== Live summary bento ===== */}
            <div className="bg-white rounded-3xl p-5 md:p-6 card-shadow lt-fade-up" data-testid="live-summary">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 font-semibold flex-wrap">
                    <span className="font-mono text-tp-violet font-bold text-sm bg-violet-50 px-2 py-0.5 rounded">{status.trainNumber}</span>
                    <span className="flex items-center gap-1 bg-amber-50 text-amber-800 font-bold px-2 py-0.5 rounded-full border border-amber-200/60">
                      <Calendar size={12} className="text-amber-600" />
                      {status.startDateLabel || journeyDate || 'Today'}
                    </span>
                  </div>
                  <h2 className="text-2xl font-extrabold text-tp-ink tracking-tight mt-1.5">{status.trainName}</h2>
                  <div className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                    {cleanName(status.from)} <ArrowRight size={13} className="text-tp-violet" /> {cleanName(status.to)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span data-testid="live-status-pill" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${onTime ? 'bg-tp-lime/15 text-tp-lime-deep' : 'bg-tp-coral/15 text-tp-coral-deep'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${onTime ? 'bg-tp-lime-deep' : 'bg-tp-coral'} lt-live-blink`} /> {fmtDelay(delayMin)}
                  </span>
                  <ShareMenu title={shareTitle} text={shareText} url={shareUrl} testid="live-share" />
                  <button onClick={() => fetchStatus(status.trainNumber, true)} disabled={refreshing} data-testid="live-refresh-btn" className="w-9 h-9 rounded-full bg-tp-violet-soft text-tp-violet flex items-center justify-center hover:bg-tp-violet hover:text-white transition-colors" title="Refresh Live Status">
                    <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Date quick-switcher inside summary bento */}
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-gray-500 font-medium">Switch Journey Date:</div>
                <div className="flex items-center gap-1.5">
                  {DAY_OPTIONS.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => handleDayChange(d.value)}
                      className={`text-xs px-2.5 py-1 rounded-lg font-bold transition-all ${
                        startDay === d.value
                          ? 'bg-[#0B2E5C] text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status message */}
              {status.statusMessage && (
                <div className="mt-4 rounded-2xl bg-tp-violet-soft/60 border border-tp-violet/10 px-4 py-2.5 text-sm font-semibold text-tp-violet-deep flex items-center gap-2">
                  <Navigation size={15} /> {status.statusMessage}
                </div>
              )}

              {/* metric bento */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  { label: 'Last Station', value: lastStation, icon: MapPin },
                  { label: 'Next Station', value: nextStation, icon: Navigation, accent: true },
                  { label: 'Expected Arrival', value: nextEta, icon: Clock, mono: true },
                  { label: 'Speed', value: `${status.speed || 0} km/h`, icon: Gauge },
                ].map((m) => (
                  <div key={m.label} className="bg-[#FAF7FF] rounded-2xl p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1"><m.icon size={11} /> {m.label}</div>
                    <div className={`font-bold mt-1 text-sm truncate ${m.accent ? 'text-tp-coral-deep' : 'text-tp-ink'} ${m.mono ? 'font-mono' : ''}`}>{m.value}</div>
                  </div>
                ))}
              </div>

              {/* progress bar */}
              <div className="mt-5">
                <div className="flex justify-between text-xs text-gray-500 mb-1.5 font-medium">
                  <span>{cleanName(status.from)}</span>
                  <span className="text-tp-violet font-bold">{Math.round(progressPct)}% journey</span>
                  <span>{cleanName(status.to)}</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full relative overflow-hidden">
                  <div className="h-full bg-gradient-violet rounded-full transition-all duration-700" style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-400 flex items-center gap-1"><Clock size={11} /> Last updated {status.lastUpdated} · TrainPanda Network</div>
            </div>

            {/* ===== Delay analytics ===== */}
            <div className="grid grid-cols-3 gap-3 lt-fade-up" style={{ animationDelay: '60ms' }} data-testid="delay-analytics">
              {[
                { label: 'Current Delay', value: onTime ? 'On Time' : `${delayMin}m`, icon: Timer, tone: onTime ? 'lime' : 'coral' },
                { label: 'Max Delay (route)', value: maxDelay > 0 ? `${maxDelay}m` : 'Nil', icon: TrendingUp, tone: maxDelay > 0 ? 'coral' : 'lime' },
                { label: 'On-time Stops', value: `${onTimeStops}/${stops.length}`, icon: CheckCircle2, tone: 'violet' },
              ].map((a) => (
                <div key={a.label} className="bg-white rounded-2xl p-3.5 card-shadow">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 ${a.tone === 'lime' ? 'bg-tp-lime/15 text-tp-lime-deep' : a.tone === 'coral' ? 'bg-tp-coral/15 text-tp-coral-deep' : 'bg-tp-violet-soft text-tp-violet'}`}>
                    <a.icon size={15} />
                  </div>
                  <div className="text-lg font-extrabold text-tp-ink leading-none">{a.value}</div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 font-bold mt-1">{a.label}</div>
                </div>
              ))}
            </div>

            {/* ===== Route timeline (also station-wise timetable) ===== */}
            <div className="bg-white rounded-3xl p-5 md:p-6 card-shadow lt-fade-up" style={{ animationDelay: '120ms' }} data-testid="route-timeline">
              <h3 className="font-extrabold text-tp-ink mb-5 flex items-center gap-2"><MapPin size={18} className="text-tp-violet" /> Route Map & Timetable</h3>
              <div className="relative">
                {stops.map((s, i) => {
                  const st = stateFor(i);
                  const name = cleanName(s.stationName || s.station);
                  const code = s.stationCode || '';
                  const dMin = parseDelayMin(s);
                  const isLast = i === stops.length - 1;
                  const lineColor = i < currentIdx ? 'bg-tp-violet' : 'bg-gray-200';
                  return (
                    <div key={`${code}-${i}`} className="flex gap-4 relative" data-testid="station-timeline-row">
                      {/* node + connector */}
                      <div className="flex flex-col items-center relative" style={{ minWidth: 40 }}>
                        {!isLast && <div className={`w-1 absolute top-5 -bottom-1 rounded-full ${lineColor}`} />}
                        {st === 'current' ? (
                          <div className="relative w-10 h-10 z-10">
                            <span className="absolute inset-0 rounded-full bg-tp-violet/30 animate-ping" />
                            <div className="relative w-10 h-10 rounded-full bg-gradient-violet flex items-center justify-center shadow-lg lt-engine-glow">
                              <Train size={19} className="text-white" />
                            </div>
                          </div>
                        ) : st === 'next' ? (
                          <div className="w-5 h-5 mt-2.5 rounded-full bg-white border-[3px] border-tp-coral z-10 lt-next-pulse" />
                        ) : st === 'completed' ? (
                          <div className="w-4 h-4 mt-3 rounded-full bg-tp-violet z-10 ring-4 ring-white" />
                        ) : (
                          <div className="w-3 h-3 mt-3.5 rounded-full bg-gray-300 z-10 ring-4 ring-white" />
                        )}
                      </div>
                      {/* details */}
                      <div className={`flex-1 flex flex-wrap gap-2 justify-between pb-7 ${isLast ? 'pb-0' : ''}`}>
                        <div className="min-w-0">
                          <div className={`font-bold truncate ${st === 'current' ? 'text-tp-violet-deep text-lg' : st === 'next' ? 'text-tp-coral-deep' : st === 'completed' ? 'text-gray-500' : 'text-gray-400'}`}>
                            {name} {st === 'current' && <span className="text-[10px] align-middle bg-tp-violet text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide ml-1">Now</span>}
                            {st === 'next' && <span className="text-[10px] align-middle bg-tp-coral text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide ml-1">Next</span>}
                          </div>
                          <div className="text-[11px] text-gray-400 flex items-center gap-2 mt-0.5">
                            {code && <span className="font-mono">{code}</span>}
                            <span>Day {s.day}</span>
                            {s.platform > 0 && <span className="font-bold text-tp-violet">PF {s.platform}</span>}
                            {s.distance > 0 && <span>{s.distance} km</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] text-gray-400">Sch <span className="font-mono">{s.schArr || s.schDep || s.sch || '--'}</span></div>
                          <div className="text-sm font-bold text-tp-ink font-mono">{s.actArr || s.actDep || s.act || '--'}</div>
                          <div className={`text-[11px] font-bold ${dMin <= 0 ? 'text-tp-lime-deep' : 'text-tp-coral-deep'}`}>{fmtDelay(dMin)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Disclaimer />
          </div>
        )}

        {!status && !loading && !error && (
          <div className="text-center py-20" data-testid="live-empty">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-tp-violet-soft text-tp-violet mb-4">
              <Gauge size={36} />
            </div>
            <h3 className="text-xl font-bold text-tp-ink">Track any Indian Railways train</h3>
            <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">Enter a train number to see its live position, an animated route map, platform info and delay analytics.</p>
          </div>
        )}

        {/* SEO Content & FAQs from Google Drive */}
        <section className="mt-12 bg-white/95 rounded-3xl p-6 md:p-8 border border-gray-100 card-shadow space-y-6 text-left">
          <div>
            <h3 className="text-xl md:text-2xl font-extrabold text-[#0B2E5C] tracking-tight">
              Track Your Train Live with Real-Time Running Status
            </h3>
            <p className="text-sm md:text-base text-gray-600 mt-2 leading-relaxed">
              Want to know where your train is right now? With TrainPanda, you can check Live Train Running Status and stay informed about your journey. Whether you are waiting at a railway station, travelling to catch a train, or checking the arrival time of a family member, real-time train information helps you plan better and avoid unnecessary waiting.
            </p>
            <p className="text-sm md:text-base text-gray-600 mt-3 leading-relaxed">
              TrainPanda makes it convenient to check live train status, find train running updates, and access useful journey information in one place. Get the latest available details about your train's position, delays, expected arrival, departure information, and route. Simply use the available train search options to find the relevant train and check its running status before or during your journey.
            </p>
            <p className="text-sm md:text-base text-gray-600 mt-3 leading-relaxed">
              If you are searching for Indian Railway train status, IRCTC train status, or a convenient way to track your train online, TrainPanda helps make railway travel information easier to access.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg font-bold text-[#0B2E5C]">
              Check Live Train Running Status Online
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Checking your train running status online is a useful way to prepare for your journey. Instead of relying only on the scheduled timetable, passengers can check the latest available running information to understand whether a train is running on time or experiencing delays.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              TrainPanda helps passengers access train status information conveniently from their mobile phones, tablets, or computers. Before leaving for the station, check your train's current status and expected arrival or departure details whenever available.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg font-bold text-[#0B2E5C]">
              Live Train Status with Train Position and Delay Updates
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Knowing your train's current running position can help you make better travel decisions. With real-time train tracking in India, passengers can check the latest available information about their train's progress along its route.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Train running updates can help you understand whether your train is delayed and estimate when it may reach your boarding station. Actual arrival times may change because of operational conditions, traffic, weather, or other railway-related factors. Always check the latest available information before making travel arrangements.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg font-bold text-[#0B2E5C]">
              How to Check Train Status by Number
            </h4>
            <p className="text-sm text-gray-600">
              Looking for train status by number? Follow these simple steps to check your train's running information on TrainPanda:
            </p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1.5 pl-1">
              <li>Visit TrainPanda Live Train Status.</li>
              <li>Use the available search option to find your train.</li>
              <li>Enter the required train details or train number, if prompted.</li>
              <li>Select the correct train from the available results.</li>
              <li>Check the latest available running status, route information, and expected arrival or departure details.</li>
            </ol>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              Checking your train status before leaving home can help you plan your station arrival and reduce uncertainty during your journey.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg font-bold text-[#0B2E5C]">
              Check Indian Railway Train Status and IRCTC Train Status
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Passengers often search for Indian Railway train status or IRCTC train status to get updates before boarding. TrainPanda provides a convenient way to access available train running information without making the process complicated.
            </p>
            <h4 className="text-lg font-bold text-[#0B2E5C] pt-2">
              Find Train Arrival and Departure Status
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Knowing your train's expected arrival and departure time is important when planning your journey. TrainPanda helps you check available train arrival status, train departure status, and train station live arrival information.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg font-bold text-[#0B2E5C]">
              Why Check Live Railway Tracking Before Your Journey?
            </h4>
            <p className="text-sm text-gray-600">
              Checking live railway tracking information before travelling can make your journey easier to manage. It helps you:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 pl-1">
              <li>Check the latest available train running status.</li>
              <li>Review expected arrival and departure information.</li>
              <li>Stay informed about reported train delays.</li>
              <li>Plan when to leave for the railway station.</li>
              <li>Keep track of journey progress and route information.</li>
              <li>Make better arrangements for station pickup and onward travel.</li>
            </ul>
            <p className="text-sm text-gray-600 mt-2">
              These simple checks can help reduce last-minute confusion and make railway travel more convenient.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="pt-6 border-t border-gray-200">
            <h4 className="text-xl font-bold text-[#0B2E5C] mb-4">
              Frequently Asked Questions (FAQs)
            </h4>
            <div className="space-y-3">
              {LIVE_STATUS_FAQS.map((faq, idx) => (
                <div key={idx} className="border border-gray-200/80 rounded-2xl overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-5 py-4 text-left font-bold text-gray-800 flex items-center justify-between gap-4 hover:text-tp-violet transition-colors"
                  >
                    <span>{idx + 1}. {faq.q}</span>
                    <span className="text-xl text-gray-400 font-light select-none">
                      {openFaq === idx ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LiveTrainStatus;
